import { useEffect, useRef } from "react";

/**
 * useEnterAnimation — one-shot enter animation (translateY 30px → 0 + opacity 0 → 1).
 *
 * PERF NOTE — SITE-WIDE SHARED SCHEDULER
 * --------------------------------------
 * Every page (services / about / contacts) uses this hook heavily — dozens of
 * elements per page. To keep the perf baseline aligned with the home page's
 * shared scroll broadcaster, this module deliberately uses:
 *
 *   · ONE global IntersectionObserver, shared by every element on the page,
 *     so we don't spawn 40+ IO instances per route.
 *   · ONE global requestAnimationFrame loop that ticks EVERY active enter
 *     animation, instead of each element scheduling its own rAF chain.
 *
 * So no matter how many elements use this hook on a given page, there is
 * always exactly ONE IO and, while animations are running, exactly ONE rAF
 * per frame. When no animations are active the loop stops entirely.
 *
 * Discipline:
 *   · No React state → zero re-renders.
 *   · DOM mutated directly via element.style.*.
 *   · Animation runs exactly once per element (unobserve on trigger).
 */

interface EnterAnimationOptions {
  delay?: number;
  distance?: number;
  duration?: number;
  threshold?: number;
}

interface AnimationEntry {
  el: HTMLElement;
  delay: number;
  distance: number;
  duration: number;
  startTime: number; // 0 until first tick
}

// ---- Shared active-animation queue + single rAF driver -----------------

const activeAnimations = new Set<AnimationEntry>();
// element → the entry currently in `activeAnimations`, so unmount cleanup
// can remove it even if the entry was created inside the shared IO callback.
const activeByEl = new WeakMap<Element, AnimationEntry>();
let rafId = 0;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const tick = (now: number) => {
  activeAnimations.forEach((entry) => {
    if (entry.startTime === 0) entry.startTime = now;
    const elapsed = now - entry.startTime - entry.delay;
    if (elapsed < 0) return; // still in the delay window
    const p = Math.min(1, elapsed / entry.duration);
    const eased = easeOutCubic(p);
    entry.el.style.opacity = eased.toFixed(3);
    entry.el.style.transform = `translate3d(0, ${(
      (1 - eased) *
      entry.distance
    ).toFixed(2)}px, 0)`;
    if (p >= 1) {
      entry.el.style.willChange = "";
      activeAnimations.delete(entry);
      activeByEl.delete(entry.el);
    }
  });

  if (activeAnimations.size > 0) {
    rafId = requestAnimationFrame(tick);
  } else {
    rafId = 0;
  }
};

const ensureRunning = () => {
  if (rafId === 0 && activeAnimations.size > 0) {
    rafId = requestAnimationFrame(tick);
  }
};

// ---- Shared IntersectionObserver ---------------------------------------

// element → animation params to enqueue when the element enters the viewport.
const pendingByEl = new WeakMap<Element, Omit<AnimationEntry, "el" | "startTime">>();

let sharedIO: IntersectionObserver | null = null;

const getIO = () => {
  if (sharedIO || typeof IntersectionObserver === "undefined") return sharedIO;
  sharedIO = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target as HTMLElement;
        const params = pendingByEl.get(el);
        if (!params) {
          observer.unobserve(el);
          continue;
        }
        pendingByEl.delete(el);
        observer.unobserve(el);
        const nextEntry: AnimationEntry = {
          el,
          delay: params.delay,
          distance: params.distance,
          duration: params.duration,
          startTime: 0,
        };
        activeAnimations.add(nextEntry);
        activeByEl.set(el, nextEntry);
        ensureRunning();
      }
    },
    { threshold: 0.15 },
  );
  return sharedIO;
};

// ---- Public hook -------------------------------------------------------

export function useEnterAnimation<T extends HTMLElement>(
  options: EnterAnimationOptions = {},
) {
  const {
    delay = 0,
    distance = 30,
    duration = 900,
    threshold = 0.15, // kept in signature for back-compat; shared IO uses 0.15
  } = options;
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Prime the "before" state directly on the DOM node.
    el.style.opacity = "0";
    el.style.transform = `translate3d(0, ${distance}px, 0)`;
    el.style.willChange = "transform, opacity";

    // If IntersectionObserver isn't available, fall back to immediate play.
    const io = getIO();
    if (!io) {
      const fallbackEntry: AnimationEntry = {
        el,
        delay,
        distance,
        duration,
        startTime: 0,
      };
      activeAnimations.add(fallbackEntry);
      activeByEl.set(el, fallbackEntry);
      ensureRunning();
      return () => {
        const current = activeByEl.get(el);
        if (current) {
          activeAnimations.delete(current);
          activeByEl.delete(el);
        }
        el.style.willChange = "";
      };
    }

    pendingByEl.set(el, { delay, distance, duration });
    io.observe(el);

    return () => {
      // If we unmount before the element ever intersected, tidy up.
      if (pendingByEl.has(el)) {
        pendingByEl.delete(el);
        io.unobserve(el);
      }
      // If it's currently animating, drop it out of the shared queue too.
      const current = activeByEl.get(el);
      if (current) {
        activeAnimations.delete(current);
        activeByEl.delete(el);
      }
      el.style.willChange = "";
    };
    // `threshold` is intentionally not a dep — shared IO uses a fixed value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, distance, duration]);

  // Reference `threshold` to keep TS happy when passed by callers.
  void threshold;

  return ref;
}